import { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { CustomerSupportContextValue, ChatbotConfig, TourConfig } from '../types';
import { loadAda, openAda } from '../adapters/ada'

const CustomerSupportContext = createContext<CustomerSupportContextValue | null>(null);

export interface CustomerSupportProviderProps {
  children: ReactNode;
  chatbotConfig?: ChatbotConfig;
  tours?: Record<string, TourConfig>;
}

export function CustomerSupportProvider({
  children,
  chatbotConfig,
  tours = {},
}: CustomerSupportProviderProps) {
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const [chatbotMessage, setChatbotMessage] = useState<string | undefined>();
  const [isTourRunning, setIsTourRunning] = useState(false);
  const [currentTourId, setCurrentTourId] = useState<string | null>(null);

  const openChatbot = useCallback((message?: string) => {
    // Set the message for the chatbot panel
    setChatbotMessage(message || chatbotConfig?.defaultMessage);

    // Try custom opener first
    if (chatbotConfig?.openChatbot) {
      chatbotConfig.openChatbot(message);
      setIsChatbotOpen(true);
      return;
    }

    // Ada integration if configured
    if (chatbotConfig?.ada?.handle) {
      void loadAda({
        handle: chatbotConfig.ada.handle,
        language: chatbotConfig.ada.language,
        meta: chatbotConfig.ada.meta,
        settings: chatbotConfig.ada.settings,
      }).then(() => openAda(message || chatbotConfig?.defaultMessage))
      setIsChatbotOpen(true);
      return
    }

    // Default behavior: open the built-in panel
    setIsChatbotOpen(true);
  }, [chatbotConfig]);

  const closeChatbot = useCallback(() => {
    setIsChatbotOpen(false);
    setChatbotMessage(undefined);
  }, []);

  const startTour = useCallback((tourId: string) => {
    const tour = tours[tourId];
    if (!tour) {
      console.warn(`[CustomerSupport] Tour "${tourId}" not found`);
      return;
    }

    console.log(`[CustomerSupport] Starting tour: ${tourId}`, tour);
    setIsTourRunning(true);
    setCurrentTourId(tourId);

    // Tour integration would go here (e.g., react-joyride)
    // For now, just log that tour started
  }, [tours]);

  const stopTour = useCallback(() => {
    setIsTourRunning(false);
    setCurrentTourId(null);
  }, []);

  const value: CustomerSupportContextValue = {
    openChatbot,
    closeChatbot,
    isChatbotOpen,
    chatbotMessage,
    startTour,
    stopTour,
    isTourRunning,
    currentTourId,
  };

  return (
    <CustomerSupportContext.Provider value={value}>
      {children}
    </CustomerSupportContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- Context provider component + hook is a legitimate pattern
export function useCustomerSupport(): CustomerSupportContextValue | null {
  const context = useContext(CustomerSupportContext);
  // Allow null context when MFE is loaded by Shell (Shell provides customer support)
  return context;
}

export default CustomerSupportContext;
